export const javascriptAccessPreviousDataMarkdown = `
### Accessing Previous Block Data

Access data from previous blocks using blockName.data:

~~~javascript
// If you have an upstream block named "patientInfo"
const patientAge = patientInfo.data.age;
const medications = patientInfo.data.medications;
~~~
`;

export const javascriptLoggingAndDebuggingMarkdown = `
### Logging and Debugging

There are logging functions available that allow you to inspect and debug your script blocks. The log output is visible in the block Output Content in a Flow Run.

The logging functions are:
~~~javascript
// For standard Info-level logging, there is a method named *log* available globally, it accepts 2 parameters
//  - message: (Required) any string, the message to be logged
//  - context: (Optional) any object that contains helpful additional context for the logged message
log('An Info message', { extra: 'context', canBe: 'random' });
log('Hello without context!');
~~~

To log at different levels, you can use the standard \`console.*\` JavaScript logging methods using the same parameters as \`log\`:
~~~javascript
console.debug('Heres a debug message');
console.info('Heres an info message with context', { a: 'apples', b: 'bananas' });
console.warn('Heres a more important warning');
console.error('Heres an error message!', { error: 'something went wrong' });
~~~
`;

export const javascriptNotableSdkMarkdown = `
### Notable SDK

Notable script blocks include an SDK with useful functions to help with common scripts. They are available in the namespace ntbl:
 - \`ntbl.dateFns\`: Date utilities ([date-fns Documentation](https://date-fns.org/docs/Getting-Started))
 - \`ntbl.dateFnsTz\`: Date utilities with timezone support ([date-fns-tz Documentation](https://github.com/marnusw/date-fns-tz))
 - \`ntbl._\`: General utilities for working with arrays, numbers, objects, strings, etc. ([More details in Lodash Documentation](https://lodash.com/docs))
      - Lodash’s modular methods are great for:
        - Iterating arrays, objects, & strings
        - Manipulating & testing values
        - Creating composite functions
 - \`ntbl.parseCsv(csvString, config?)\`: Parse a CSV string using Papa Parse ([Papa Parse Documentation](https://www.papaparse.com/docs))
      - *Note*: The config options _download_ and _worker_ are not allowed in this environment,
      as parsing from remote URLs or using an asynchronous worker parsing are restricted in this environment
 - \`ntbl.unparseCsv(dataOrArray, config?)\`: Unparse JSON data into a CSV string using Papa Parse ([Papa Parse Documentation](https://www.papaparse.com/docs))

To use an sdk function like Lodash's [\`difference\`](https://lodash.com/docs/4.17.15#difference):
~~~javascript
const firstArray = ['a', 'b', 'c'];
const secondArray = ['b', 'c', 'd'];
const difference = ntbl._.difference(firstArray, secondArray);
// difference: ['a'] (i.e. the items in firstArray that are not in secondArray, ignoring any extras)
~~~
`;

interface JavaScriptTipsAndNotesOptions {
  errorHandlingVariant: 'ReturnObject' | 'ThrowError';
}
export function getJavaScriptTipsAndNotesMarkdown(options: JavaScriptTipsAndNotesOptions): string {
  const { errorHandlingVariant } = options;
  const errorHandlingSnippet =
    errorHandlingVariant === 'ReturnObject'
      ? `
  return {
    error: "Missing patient data"
  };
`
      : `
  throw new Error("Missing patient data");
`;

  return `
Important Tips
--------------

### 1. Error Handling

Always check if data exists before using it:

~~~javascript
if (!patientData.data) {${errorHandlingSnippet}}
~~~

### 2. Best Practices

*   Keep your code simple and focused on one task
*   Add comments to explain complex logic
*   Use meaningful variable names
*   Test with different scenarios
*   Always include a return statement
*   Double-check block names when referencing upstream blocks
*   Break down complex operations into smaller steps

### 3. Getting Help

*   Use AI assistants like Claude or ChatGPT to:
    *   Write code for specific tasks
    *   Debug issues
    *   Convert logic into JavaScript

Security and Environment Notes
------------------------------

*   The script runs in a protected environment
*   Only certain JavaScript operations are allowed
*   External API calls are not permitted
*   No File system access
*   Limited to core JavaScript and provided SDK functionality
`;
}
